import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4arscbbpm5d1fqikheg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contact Ocean View Inn & Sports Pub",
    "description": "Get in touch with Ocean View Inn & Sports Pub in Islamorada, Florida Keys for reservations, inquiries, or information",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
    "mainEntity": {
      "@type": "Restaurant",
      "name": "The Ocean View Inn & Sports Pub",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}`,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "84500 Overseas Hwy",
        "addressLocality": "Islamorada",
        "addressRegion": "FL",
        "postalCode": "33036",
        "addressCountry": "US"
      },
      "telephone": "(305) 664-8052",
      "email": "resinfo@theocean-view.com",
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 24.9470979,
        "longitude": -80.6019239
      },
      "sameAs": [
        "https://www.facebook.com/The-OceanView-Motel-and-Sports-Pub-176655585715394",
        "https://www.instagram.com/oceanview_inn_pub/"
      ],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday"],
          "opens": "11:00",
          "closes": "22:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Friday", "Saturday"],
          "opens": "11:00",
          "closes": "01:00"
        }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Contact Us - Ocean View Inn & Sports Pub | Islamorada, Florida Keys"
        description="Contact Ocean View Inn & Sports Pub in Islamorada for reservations, inquiries, and information about our restaurant and hotel. Located at 84500 Overseas Highway, MM 84."
        keywords="contact ocean view inn, islamorada contact, florida keys hotel contact, sports pub islamorada"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`}
        ogTitle="Contact Ocean View Inn & Sports Pub"
        ogDescription="Contact Ocean View Inn & Sports Pub in Islamorada for reservations, inquiries, and information about our restaurant and hotel."
        ogImage="https://public.readdy.ai/ai/img_res/6734e616e4d2d6b06cf02c7fbcbdf43e.jpg"
        ogUrl={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`}
        structuredData={structuredData}
        geoPosition="24.9470979;-80.6019239"
        geoRegion="US-FL"
        geoPlacename="Islamorada, Florida"
      />
      <Header />
      <main className="pt-20">
        <section className="relative h-[400px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://static.readdy.ai/image/a0544820e0625081891e0a711cac5596/43ce92451a57c4d2ad2bdbe7a9f250c9.jpeg)',
            }}
          >
            <div className="absolute inset-0 bg-slate-900/60"></div>
          </div>
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold mb-4 font-serif">Contact Us</h1>
            <p className="text-xl text-slate-200">
              Get in touch with us for reservations, inquiries, or just to say hello
            </p>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="text-4xl font-bold text-slate-900 mb-6 font-serif">
                  Send Us a Message
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  Have a question about our rooms, menu, or events? Fill out the form below and we'll get back to you as soon as possible.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      maxLength={20}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm"
                      placeholder="(305) 555-0123"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-slate-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      maxLength={100}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all text-sm"
                      placeholder="Room Reservation Inquiry"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                      Your Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={6}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent transition-all resize-none text-sm"
                      placeholder="Tell us how we can help you..."
                    ></textarea>
                    <p className="text-xs text-slate-500 mt-1">
                      {formData.message.length}/500 characters
                    </p>
                  </div>

                  {submitStatus === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 text-green-800">
                        <i className="ri-check-line text-xl"></i>
                        <p className="font-medium">Message sent successfully! We'll get back to you soon.</p>
                      </div>
                    </div>
                  )}

                  {submitStatus === 'error' && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 text-red-800">
                        <i className="ri-error-warning-line text-xl"></i>
                        <p className="font-medium">Something went wrong. Please try again or call us directly.</p>
                      </div>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg font-semibold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none whitespace-nowrap"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <i className="ri-loader-4-line animate-spin mr-2"></i>
                        Sending...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <i className="ri-send-plane-line mr-2"></i>
                        Send Message
                      </span>
                    )}
                  </button>
                </form>
              </div>

              <div>
                <h2 className="text-4xl font-bold text-slate-900 mb-6 font-serif">
                  Contact Information
                </h2>
                <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                  Visit us in Islamorada or reach out through any of the following channels. We're here to help make your Florida Keys experience unforgettable!
                </p>

                <div className="space-y-6 mb-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Address</h3>
                      <p className="text-slate-600">84500 Overseas Hwy<br />Islamorada, FL 33036</p>
                      <a
                        href="https://maps.google.com/maps?q=84500+Overseas+Hwy+Islamorada,+FL+33036"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-1 inline-block"
                      >
                        Get Directions →
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Phone</h3>
                      <a href="tel:+13056648052" className="text-slate-600 hover:text-blue-600 transition-colors">
                        (305) 664-8052
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Email</h3>
                      <a href="mailto:resinfo@theocean-view.com" className="text-slate-600 hover:text-blue-600 transition-colors">
                        resinfo@theocean-view.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-2xl text-blue-600"></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">Hours</h3>
                      <p className="text-slate-600">
                        Sun-Thu: 11:00 AM - 10:00 PM<br />
                        Fri-Sat: 11:00 AM - 1:00 AM
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-900 rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Follow Us</h3>
                  <p className="text-slate-300 mb-6">
                    Stay updated on events, specials, and Keys happenings!
                  </p>
                  <div className="flex space-x-4">
                    <a
                      href="https://www.facebook.com/The-OceanView-Motel-and-Sports-Pub-176655585715394"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                      aria-label="Facebook"
                    >
                      <i className="ri-facebook-fill text-2xl"></i>
                    </a>
                    <a
                      href="https://www.instagram.com/oceanview_inn_pub/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-pink-600 rounded-full flex items-center justify-center hover:bg-pink-700 transition-colors"
                      aria-label="Instagram"
                    >
                      <i className="ri-instagram-fill text-2xl"></i>
                    </a>
                  </div>
                </div>

                <div className="mt-8 rounded-2xl overflow-hidden shadow-xl">
                  <iframe
                    src="https://maps.google.com/maps?f=q&source=s_q&hl=en&geocode=&q=+84500+Overseas+Hwy+Islamorada,+FL+33036&ie=UTF8&t=m&z=15&output=embed"
                    width="100%"
                    height="300"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="The Ocean View Inn & Sports Pub Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
